import { Box, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { Dropdown, LoadingSpinner } from '@verantos-dev/shared-components';

import { BatchNotSelected } from './components/BatchNotSelected';
import { IRRScoreCards } from './components/IRRScoreComponents/IRRScoreCards';
import { IRRScoreDataTable } from './components/IRRScoreComponents/IRRScoreDataTable';
import { useIRRScoreCards } from './components/IRRScoreComponents/useIRRScoreCards';
import { MetricProjectsDropdown } from './components/MetricProjectsDropdown';
import { ProjectNotSelected } from './components/ProjectNotSelected';
import { useMetricIRRScore } from './useMetricIRRScore';

interface MetricIRRScoreProps {
  hideProjectSelector?: boolean;
}

export const MetricIRRScore = ({ hideProjectSelector = false }: MetricIRRScoreProps = {}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    irrScoreDateRange,
    isIRRScoreDateRangeLoading,
    date,
    handleChangeDate,
    shouldDisableDate,
    batchTypeOptions,
    batchTypeId,
    isMetricsBatchTypeListLoading,
    handleBatchTypeChange,
    projectId,
    filterTypeOptions,
    filterTypeId,
    handleFilterTypeChange,
    isFilterTypeListLoading,
  } = useMetricIRRScore();
  const { isIRRScoreCardsDataLoading } = useIRRScoreCards();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;
  const isLoading =
    isIRRScoreDateRangeLoading ||
    isMetricsBatchTypeListLoading ||
    isFilterTypeListLoading ||
    isIRRScoreCardsDataLoading;

  return (
    <Box
      sx={{ p: theme.spacing(6), display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}
    >
      {!hideProjectSelector && (
        <Box sx={{ mb: theme.spacing(12) }}>
          <Typography variant="h4">{formatMessage({ id: 'metrics.irrScore.title' })}</Typography>
        </Box>
      )}
      {!hideProjectSelector && <MetricProjectsDropdown />}
      <Box
        id="metrics-irr-score-content"
        sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}
      >
        <Box sx={{ display: 'flex', gap: theme.spacing(6) }}>
          <Dropdown
            id="metric-irr-score-filter-type-dropdown"
            options={filterTypeOptions}
            onChange={handleFilterTypeChange}
            label={formatMessage({ id: 'metrics.irrScore.typeFilter.label' })}
            placeholder={formatMessage({
              id: 'metrics.irrScore.typeFilter.label',
            })}
            disabled={isFilterTypeListLoading || filterTypeOptions.length === 0 || !projectId}
            width="medium"
            value={filterTypeId ?? ''}
          />
          <DatePicker
            label={formatMessage({ id: 'metrics.irrScore.datepicker.label' })}
            value={date}
            onChange={(newValue) => handleChangeDate(newValue)}
            minDate={dayjs(irrScoreDateRange?.minDate)}
            maxDate={dayjs(irrScoreDateRange?.maxDate)}
            shouldDisableDate={shouldDisableDate}
            disabled={isIRRScoreDateRangeLoading || !projectId || !batchTypeId}
            slotProps={{
              textField: {
                sx: {
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                },
              },
            }}
          />
          <Dropdown
            id="metric-irr-score-batch-type-dropdown"
            options={batchTypeOptions}
            onChange={handleBatchTypeChange}
            label={formatMessage({ id: 'workspaceCommon.content.dropdown.batch.label' })}
            placeholder={formatMessage({
              id: 'workspaceCommon.content.dropdown.batch.placeholder',
            })}
            disabled={isMetricsBatchTypeListLoading || batchTypeOptions.length === 0 || !projectId}
            width="medium"
            value={batchTypeId ?? ''}
          />
        </Box>
        <Box>
          {!hideProjectSelector && !projectId ? (
            <ProjectNotSelected />
          ) : !batchTypeId ? (
            <BatchNotSelected />
          ) : showLoadingSpinner && isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '400px' }}>
              <LoadingSpinner id="metricIRRScoreLoading" />
            </Box>
          ) : (
            <Box>
              <IRRScoreCards />
              <IRRScoreDataTable />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
